/**
 * @file 画像 1 枚を画面いっぱいに出すビューワ (ページ画像・問の画像の共通)。
 *
 * immersive を渡すとリーダーそのものとして振る舞う (本を読む画面の土台)。Esc と余白のタップで
 * 閉じず、閉じるボタンは「本だなにもどる」のような呼び出し側の行き先になる。サイドパネルは
 * panels で複数 (メモ / 目次) を渡せ、同時に開くのはひとつで、ヘッダーのボタンで切り替える。
 *
 * subBar はヘッダーの下、bottomBar は紙面の下に出す1段である。
 *
 * @what: 画面の縦幅に合わせて画像を収め (横幅が足りなければ横幅に合わせる)、上に題と
 *   「閉じる」、紙面の左右に「前へ / 次へ」を重ねる。Esc と余白のタップ (押し始めも離した先も
 *   余白のとき) で閉じる。開いている間は
 *   後ろの画面をスクロールさせず、閉じたら元のボタンへ焦点を戻す。
 *   notePane を渡すと「メモ」トグルが出て、画像の隣に学びメモのペインを並べる。広い画面
 *   (900px 以上) では開いた状態で始まり (代表 2026-08-29)、狭い画面ではページとメモの
 *   全画面切り替えになる。
 *   immersive のときは紙面のタップで上下の操作の段を隠し、紙面だけにする。隠している間は
 *   上端のつまみと Esc で戻せる (代表の判断、2026-09-11)。紙面に重ねた「前へ / 次へ」は
 *   紙面のタップとして数えないので、めくっても隠したままか出したままかは変わらない。
 *   紙面の左右に矢印を置く余白が無いとき (スマホの縦画面・iPad の縦画面) は矢印を出さず、
 *   紙面の左1/3のタップで前へ、右1/3で次へ、中央1/3で操作の段の出し入れにする
 *   (Kindle の作法、代表の判断、2026-09-17)。※なぜなら重ねた矢印が本文にかぶるためである。
 *   余白の有無は hooks/useNavRoom.ts で実測し、測れないときは矢印を出す。
 *   紙面を触る道具が入っている間はタップが線や囲みになるので、隠す操作は道具の段の
 *   「紙面を広く」だけにする。この間は紙面に重ねた「前へ / 次へ」も出さず (書く場所に
 *   重なるため)、代わりに道具の段の右端に置く。※なぜなら矢印キーの無い iPad では、
 *   書き込みながらページを送る手段が無くなるためである (代表の指摘、2026-09-17)。
 *   「紙面を広く」で段を消している間は道具の段も無く、紙面のタップは線になるので、
 *   上端のつまみの両脇に小さな「前へ / 次へ」を置く (紙面に重なる面積を最小にする)。
 *   狭い画面 (900px 未満) では「本だなへ戻る ＋ 題」の行と操作1行の2行に収め、primary の
 *   付いていない操作とパネル、ページ番号の入力は⋯のメニュー (ViewerMenu) へ畳む。
 *   immersive のときは紙面の右下に、小さくする・いまの倍率・大きくするの3つを置く。大きくすると
 *   紙面の枠の中でスクロールして見る。倍率はページを送っても変わらない。
 *   spread を渡すと、もう1枚を隣に並べて見開きにする。2枚は縦横比を保ったまま同じ高さで接し、
 *   紙面の枠に収まる最大の大きさで出す。倍率は2枚をまとめて変える。
 * @why: 紙 1 ページの画像は右の欄と並べると縮んで文字が読みにくい。タップで全画面にし、
 *   縦幅いっぱいで読めるようにする (代表 2026-08-23)。OS の全画面 API は iOS Safari が
 *   画像に使えないので、画面を覆う要素で作る (ピンチでの拡大は OS に任せる)。
 *   操作が増えてスマホの縦画面では紙面が半分近くまで縮んだので、読む間は操作を消せるように
 *   し、ページ送りは行を使わず紙面に重ねた (代表の判断、2026-09-11)。
 * @failure: 描画と、紙面だけにしているかの保持だけを持つ。画像の取得状態 (読み込み中 / 失敗)
 *   は親から受け取ってそのまま出す。stageMessage を渡したときだけ、取得状態によらずその文言を
 *   画像の場所に出す。
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { ScanImageState } from "../hooks/useScanImage";
import { useDecodedImageUrl } from "../hooks/useDecodedImageUrl";
import { useNavRoom } from "../hooks/useNavRoom";
import { useWideScreen, wideScreen } from "../lib/wideScreen";
import { PullDownIcon } from "./readerIcons";
import { ViewerMenu } from "./ViewerMenu";
import "./study.css";

/**
 * サイドパネルの1枚である (ヘッダーの切り替えの文言と中身)。kind が "mode" のものはサイドパネルを
 * 開かず、選ばれていることだけを親に伝える (ページに書き込む)。
 */
export interface ViewerPanel {
  id: string;
  label: string;
  content?: ReactNode;
  kind?: "pane" | "mode";
  /** 狭い画面でもヘッダーに出す (付けないものは⋯のメニューへ入る)。 */
  primary?: boolean;
}

/**
 * ヘッダーに並べる操作の1つである。
 *
 * 広い画面では group ごとにまとめて並べ、狭い画面では primary のものだけを残して、ほかは
 * ⋯のメニューへ入れる。※なぜならスマホの縦画面ではヘッダーが5行を超え、紙面が半分近くまで
 * 縮むためである (代表の指摘、2026-09-11)。
 */
export interface ViewerControl {
  id: string;
  label: string;
  /** 読み上げの名前 (文言だけでは何に対する操作か分からないとき)。 */
  ariaLabel?: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** 押している間だけ塗るトグルのとき。 */
  pressed?: boolean;
  /** 右肩に出す件数 (AI が答えたこと)。 */
  count?: number;
  tone?: "pick" | "ai" | "quiet";
  /** 群の名前である。続けて同じ名前のものを1つの群にまとめる。 */
  group: string;
  primary?: boolean;
}

/**
 * 続けて同じ群の名前を持つものを、1つの群にまとめる。
 *
 * key は群を作ったときの先頭の id である。中身が空の群は作らないので、描画の側で
 * 先頭を取り直さずに済む。
 */
function byGroup(
  controls: readonly ViewerControl[],
): { key: string; group: string; items: ViewerControl[] }[] {
  const out: { key: string; group: string; items: ViewerControl[] }[] = [];
  for (const c of controls) {
    const last = out[out.length - 1];
    if (last && last.group === c.group) last.items.push(c);
    else out.push({ key: `${c.group}:${c.id}`, group: c.group, items: [c] });
  }
  return out;
}

function controlClass(tone: ViewerControl["tone"]): string {
  const suffix = tone === undefined ? "" : ` study-viewer-btn--${tone}`;
  return `study-viewer-btn${suffix}`;
}

/**
 * 見開きで隣に並べる1枚である (読む順で後のページ)。
 *
 * onLeft は、その1枚を左に置くか (右開きの本)。overlay はその1枚の上に重ねるもの (書き込みの表示)。
 */
export interface ViewerSpreadPage {
  image: ScanImageState;
  alt: string;
  onLeft: boolean;
  overlay?: ReactNode;
}

/** 縦横比が分かる前に仮に使う値である (A 判・B 判の縦長の紙)。 */
const PAPER_ASPECT = 1 / Math.SQRT2;

/**
 * 紙面の大きさの段である。1が画面に収めた大きさで、そこから3倍まで上げられる。
 *
 * 刻みを決め打ちにするのは、押すたびに読める大きさへ確実に届くようにするためである。
 */
const ZOOM_STEPS = [1, 1.25, 1.5, 2, 2.5, 3];

/**
 * 紙面の外の余白を押したかを返す (器そのもの・拡大の器・紙面の枠)。
 *
 * 拡大のために枠を挟んだので、余白の押し先は器そのものとは限らない。画像・書き込みの層・
 * ボタンの上は余白としない。
 */
function onStageBackdrop(target: EventTarget | null, stage: EventTarget): boolean {
  if (target === stage) return true;
  if (!(target instanceof Element)) return false;
  return (
    target.classList.contains("study-viewer-scroll") ||
    target.classList.contains("study-viewer-canvas")
  );
}

export function FullscreenViewer({
  image,
  alt,
  label,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  notePane,
  panels,
  defaultPanelId,
  activePanelId,
  onPanelChange,
  controls,
  headerExtra,
  menuExtra,
  plainAction,
  subBar,
  bottomBar,
  overlay,
  persistentPane,
  stageMessage,
  closeLabel = "閉じる",
  shortCloseLabel,
  immersive = false,
  paperTool = false,
  sidePane,
  spread,
}: {
  image: ScanImageState;
  alt: string;
  /** 上に出す題である (p.16 / 0020_a / 本の題名など)。 */
  label: string;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  /** 学びメモのペイン (StudyNotePane)。渡すと「メモ」のパネルになる (panels の省略形)。 */
  notePane?: ReactNode;
  /** サイドパネルの一覧 (メモ / 目次 など)。同時に開くのはひとつである。 */
  panels?: readonly ViewerPanel[];
  /** 広い画面で最初から開いておくパネル (省略 = 最初のパネル、null = 開かない)。 */
  defaultPanelId?: string | null;
  /** 選ばれているパネルを親が持つとき (省略なら内部で持つ)。 */
  activePanelId?: string | null;
  onPanelChange?: (id: string | null) => void;
  /** ヘッダーのパネル切り替えの左に並べる操作である。 */
  controls?: readonly ViewerControl[];
  /** ヘッダーの題の右に差し込むもの (いま何枚目かなど)。狭い画面でも題の行に残す。 */
  headerExtra?: ReactNode;
  /**
   * ヘッダーの題の右に差し込む入力である (ページ番号)。
   * 狭い画面では⋯のメニューの中へ入れる。※なぜなら入力の枠は横幅を取り、題の行に
   * 収まらないためである。
   */
  menuExtra?: ReactNode;
  /**
   * 操作の並びの先頭に置く、読む画面へ戻る導線である (「読むだけにもどる」「選択中 n件」)。
   * ふつうの読む画面から外れているときだけ親が渡す。
   */
  plainAction?: ReactNode;
  /** ヘッダーの下に出す1段 (書き込みの道具など)。 */
  subBar?: ReactNode;
  /** 下のナビの上に出す1段 (選んで聞く質問欄)。 */
  bottomBar?: ReactNode;
  /**
   * 切り替えても消さないサイドペインである。visibleFor のパネルが選ばれているときだけ見せ、
   * それ以外は隠して (display: none) 状態と自動保存を保つ。
   */
  persistentPane?: { visibleFor: readonly string[]; content: ReactNode };
  /**
   * パネルの切り替えとは別に、紙面の隣へ出す列である (本の答え)。渡している間は、選ばれている
   * パネルより優先してこの列を出す。※なぜなら「ページに書き込む」のようにパネルの枠を使う
   * 切り替えと同時に開いておきたいためである。
   */
  sidePane?: ReactNode;
  /** 画像の上に重ねるもの (書き込みの層)。画像の枠の中に置く。 */
  overlay?: ReactNode;
  /** 画像の代わりに出す文言である (しぼりこみに当たる問が無いときなど)。 */
  stageMessage?: ReactNode;
  /** 閉じるボタンの文言 (immersive では「本だなにもどる」など)。 */
  closeLabel?: string;
  /** 狭い画面で題の行に置くときの短い文言 (省略すると closeLabel のまま)。 */
  shortCloseLabel?: string;
  /** リーダーそのものとして出す。Esc と余白のタップで閉じず、焦点も動かさない。 */
  immersive?: boolean;
  /**
   * 紙面を直に触る道具が入っているか (範囲を囲む・ページに書き込む)。
   * 入っている間はタップで操作の段を切り替えず、ページ送りも紙面から外す。隠すときは道具の段の
   * 「紙面を広く」を押し、上端のつまみと Esc で戻す。
   * ※なぜならタップは線や囲みになり、手のひらが当たっただけで段が消えては読めなくなるためである。
   * 手のひらが紙面の外に当たっても文字を選ばないよう、この間は選択を止める (study-viewer--paper)。
   */
  paperTool?: boolean;
  /** 見開きで隣に並べる1枚 (渡さなければ1枚で出す)。 */
  spread?: ViewerSpreadPage;
}) {
  const allPanels: readonly ViewerPanel[] =
    panels ?? (notePane !== undefined ? [{ id: "note", label: "メモ", content: notePane }] : []);
  const initialPanel = defaultPanelId === undefined ? (allPanels[0]?.id ?? null) : defaultPanelId;
  const [innerPanelId, setInnerPanelId] = useState<string | null>(() =>
    wideScreen() ? initialPanel : null,
  );
  const openPanelId = activePanelId === undefined ? innerPanelId : activePanelId;
  const selectPanel = (id: string | null) => {
    if (activePanelId === undefined) setInnerPanelId(id);
    onPanelChange?.(id);
  };
  const closeRef = useRef<HTMLButtonElement>(null);
  // 紙面の大きさ (リーダーとして出しているときだけ変えられる)。ページを送っても持ち続け、
  // 開いている間はその大きさのまま読める (代表の判断、2026-09-13)。端末には覚えない。
  const [zoomStep, setZoomStep] = useState(0);
  const canZoom = immersive;
  const zoom = canZoom ? (ZOOM_STEPS[zoomStep] ?? 1) : 1;
  // 大きくした紙面を送るための器である。倍率を変えたときとページを送ったときに、見ている
  // ところをここで決める。
  const scrollRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const shownZoom = useRef(zoom);
  // 倍率を変えても、見ていたところの中心は変えない。
  // ※なぜなら大きくするのは読めない字を読むためであり、押すたびに端へ飛ぶと探し直しになるためである。
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const ratio = zoom / shownZoom.current;
    shownZoom.current = zoom;
    if (!el || ratio === 1) return;
    el.scrollLeft = (el.scrollLeft + el.clientWidth / 2) * ratio - el.clientWidth / 2;
    el.scrollTop = (el.scrollTop + el.clientHeight / 2) * ratio - el.clientHeight / 2;
  }, [zoom]);
  // ページを送ったら、横は中央・縦は上端から見せる (大きさはそのまま)。
  // 紙面に出すのはデコードを終えた画像である。次のページの取得中とデコード中は前のページを残す。
  const imageUrl = useDecodedImageUrl(image.status === "ready" ? image.url : null);
  // 見開きの隣の1枚も、デコードを終えたものだけを出す (取得中は前の見開きの1枚を残す)。
  const pairUrl = useDecodedImageUrl(spread?.image.status === "ready" ? spread.image.url : null);
  // 前のページを残したまま、次のページの取得を待っているか。
  const waitingNext =
    (image.status === "loading" && imageUrl !== null) ||
    (spread !== undefined && spread.image.status === "loading" && pairUrl !== null);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
  }, [imageUrl]);
  // 2枚の縦横比 (幅 / 高さ)。並べる幅の割り振りに使う。読み込むたびに測り直し、それまでは前の値を使う。
  const [aspects, setAspects] = useState({ main: PAPER_ASPECT, pair: PAPER_ASPECT });
  const spreadRef = useRef<HTMLDivElement>(null);
  const spreadShown = spread !== undefined && !stageMessage && image.status !== "error" && imageUrl !== null;
  // 紙面の左右に矢印を置く余白があるか (null は測れていない)。見開きのときは2枚の並びで測る。
  const navRoom = useNavRoom(
    stageRef,
    spreadShown ? spreadRef : imgRef,
    imageUrl === null ? null : spreadShown ? `${imageUrl}#spread` : imageUrl,
    zoom,
  );
  // 紙面だけにしているか (リーダーとして出しているときだけ切り替わる)。
  const [chromeHidden, setChromeHidden] = useState(false);
  // Esc の購読は1回しか張らないので、いまの値は ref から読む。
  const chromeHiddenRef = useRef(chromeHidden);
  chromeHiddenRef.current = chromeHidden;
  // 紙面を触る道具が入ったら、いったん操作を出し直す (道具の状態が読めなくなるのを防ぐ)。
  // そのあと「紙面を広く」を押せば、書いたまま上下の段を消せる。
  useEffect(() => {
    if (paperTool) setChromeHidden(false);
  }, [paperTool]);
  // 余白のタップで閉じるのは、押し始めも離した先も余白だったときだけ (画像の上で押して
  // 余白で離した、開いた直後に届いた 2 つ目の click、のどちらでも閉じない)。
  const pressedOnBackdrop = useRef(false);
  // 親が毎回新しい関数を渡しても、開閉の副作用 (焦点・スクロール固定・Esc) は 1 回だけ張る。
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    if (!immersive) closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // リーダーとして出しているときの Esc は、隠した操作を出し直すことに使う (閉じない)。
      if (immersive) {
        if (chromeHiddenRef.current) {
          e.stopPropagation();
          setChromeHidden(false);
        }
        return;
      }
      e.stopPropagation();
      onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (!immersive && previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [immersive]);

  // 狭い画面は「題 ＋ 本だな」の行と、操作1行に収める。残りは⋯のメニューへ入れる。
  const narrow = !useWideScreen();
  const allControls = controls ?? [];
  const inlineControls = narrow ? allControls.filter((c) => c.primary) : allControls;
  const menuControls = narrow ? allControls.filter((c) => !c.primary) : [];
  const inlinePanels = narrow ? allPanels.filter((p) => p.primary) : allPanels;
  const menuPanels = narrow ? allPanels.filter((p) => !p.primary) : [];
  const hasMenu = menuControls.length > 0 || menuPanels.length > 0 || (narrow && menuExtra);

  const openPanel =
    allPanels.find((p) => p.id === openPanelId && p.kind !== "mode" && p.content !== undefined) ?? null;
  const persistentVisible =
    persistentPane !== undefined && openPanelId !== null && persistentPane.visibleFor.includes(openPanelId);
  const sideVisible = sidePane !== undefined || openPanel !== null || persistentVisible;
  // 紙面だけにできるのは、リーダーとして出しているときである。紙面を触る道具が入っている間は
  // 紙面のタップが線になるので、消す操作は道具の段の「紙面を広く」だけにする。
  const canHideChrome = immersive;
  const bare = canHideChrome && chromeHidden;
  const hasNav = onPrev !== undefined || onNext !== undefined;
  // 余白が無いときは矢印を出さず、紙面の左右のタップで送る (道具が入っている間は線になるので使わない)
  const tapZones = immersive && hasNav && !paperTool && navRoom === false;
  const rootClass = [
    "study-scope study-viewer",
    immersive ? "study-viewer--immersive" : "",
    paperTool ? "study-viewer--paper" : "",
    sideVisible ? "study-viewer--panel-open" : "",
    subBar ? "study-viewer--with-subbar" : "",
    bottomBar ? "study-viewer--with-bottombar" : "",
    bare ? "study-viewer--bare" : "",
    spreadShown ? "study-viewer--spread" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const rootProps = immersive
    ? { role: "region" as const, "aria-label": `${label} を読む` }
    : { role: "dialog" as const, "aria-modal": true, "aria-label": `${label} を全画面で表示` };
  // リーダーでは本を出る導線を題の左に置く。狭い画面では短い文言にする (題の行に収めるため)。
  const closeText = narrow ? (shortCloseLabel ?? closeLabel) : closeLabel;
  const closeButton = (
    <button
      ref={closeRef}
      type="button"
      className={immersive ? "study-viewer-btn study-viewer-btn--back" : "study-viewer-btn"}
      onClick={onClose}
    >
      {immersive ? `← ${closeText}` : closeText}
    </button>
  );

  // decoding="sync" は、デコードを済ませた結果を WebKit がメモリの都合で捨てていたときの備えである。
  // 表示の時点でデコードし直してから出すので、白い枠だけのフレームが出ない。
  const mainImg = (
    <img
      ref={imgRef}
      src={imageUrl ?? undefined}
      alt={alt}
      className="study-viewer-img"
      decoding="sync"
      onLoad={(e) => {
        const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
        if (w > 0 && h > 0) setAspects((a) => (a.main === w / h ? a : { ...a, main: w / h }));
      }}
    />
  );
  const mainOverlay = image.status === "ready" && imageUrl === image.url && !stageMessage ? overlay : null;
  const pairLeaf = (page: ViewerSpreadPage) => (
    <div className="study-viewer-leaf" style={{ flexGrow: aspects.pair }}>
      {page.image.status === "error" ? (
        <p role="alert" className="study-viewer-msg">
          画像を読み込めませんでした。
        </p>
      ) : pairUrl === null ? (
        <span className="study-viewer-leaf-blank" aria-hidden="true" />
      ) : (
        <img
          src={pairUrl}
          alt={page.alt}
          className="study-viewer-img"
          decoding="sync"
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w > 0 && h > 0) setAspects((a) => (a.pair === w / h ? a : { ...a, pair: w / h }));
          }}
        />
      )}
      {page.image.status === "ready" && pairUrl === page.image.url ? page.overlay : null}
    </div>
  );

  return createPortal(
    <div className={rootClass} {...rootProps}>
      <div className="study-viewer-bar">
        {/* リーダーでは本を出る導線を左端に置く (戻る操作の定位置に合わせる) */}
        {immersive ? closeButton : null}
        <span className="study-viewer-label" title={label}>
          {label}
        </span>
        {headerExtra}
        {narrow ? null : menuExtra}
        {narrow && !immersive ? closeButton : null}
        <span className="study-viewer-bar-actions">
          {plainAction}
          {byGroup(inlineControls).map(({ key, group, items }) => (
            <span key={key} className="study-viewer-group" role="group" aria-label={group}>
              {items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={controlClass(c.tone)}
                  aria-label={
                    c.count !== undefined ? `${c.label}（${c.count}件）` : c.ariaLabel
                  }
                  {...(c.pressed === undefined ? {} : { "aria-pressed": c.pressed })}
                  onClick={c.onSelect}
                >
                  {c.icon}
                  <span className="study-viewer-btn-label">{c.label}</span>
                  {c.count === undefined ? null : (
                    <span className="study-viewer-btn-count" aria-hidden="true">
                      {c.count}
                    </span>
                  )}
                </button>
              ))}
            </span>
          ))}
          {inlinePanels.length > 0 ? (
            <span className="study-viewer-seg" role="group" aria-label="表示の切り替え">
              {inlinePanels.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="study-viewer-seg-btn"
                  aria-pressed={openPanelId === p.id}
                  onClick={() => selectPanel(openPanelId === p.id ? null : p.id)}
                >
                  {p.label}
                </button>
              ))}
            </span>
          ) : null}
          {hasMenu ? (
            <ViewerMenu
              controls={menuControls}
              panels={menuPanels}
              openPanelId={openPanelId}
              onSelectPanel={(id) => selectPanel(openPanelId === id ? null : id)}
              extra={menuExtra}
            />
          ) : null}
          {narrow || immersive ? null : closeButton}
        </span>
      </div>
      {subBar ? (
        <div className="study-viewer-subbar">
          {subBar}
          {hasNav && paperTool && !bare ? (
            <span className="study-viewer-subnav" role="group" aria-label="ページ送り">
              <button
                type="button"
                className="study-viewer-btn study-viewer-btn--small"
                aria-label="前へ"
                onClick={onPrev}
                disabled={!hasPrev}
              >
                <span aria-hidden="true">←</span> 前へ
              </button>
              <button
                type="button"
                className="study-viewer-btn study-viewer-btn--small"
                aria-label="次へ"
                onClick={onNext}
                disabled={!hasNext}
              >
                次へ <span aria-hidden="true">→</span>
              </button>
            </span>
          ) : null}
          {canHideChrome && paperTool ? (
            <button
              type="button"
              className="study-viewer-btn study-viewer-btn--small study-viewer-wide-btn"
              onClick={() => setChromeHidden(true)}
            >
              紙面を広く
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="study-viewer-body">
        <div
          ref={stageRef}
          className="study-viewer-stage"
          onPointerDown={(e) => {
            pressedOnBackdrop.current = onStageBackdrop(e.target, e.currentTarget);
          }}
          onClick={(e) => {
            const onBackdrop = onStageBackdrop(e.target, e.currentTarget) && pressedOnBackdrop.current;
            pressedOnBackdrop.current = false;
            if (onBackdrop && !immersive) {
              onClose();
              return;
            }
            // 紙面に重ねたボタン (ページ送り) を押したときは、紙面のタップとして数えない。
            // ※なぜならページを送るたびに操作の段が出たり消えたりして、読んでいる形が
            //   ページごとに入れ替わってしまうためである (代表の指摘、2026-09-13)。
            if (e.target instanceof Element && e.target.closest("button, a, input, textarea, select"))
              return;
            // 矢印を出していないときは、紙面の左1/3で前へ、右1/3で次へ (中央は段の出し入れ)。
            if (tapZones) {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;
              if (x < 1 / 3) {
                if (hasPrev) onPrev?.();
                return;
              }
              if (x > 2 / 3) {
                if (hasNext) onNext?.();
                return;
              }
            }
            // 紙面のタップで操作の段を消したり出したりする。ページ送りは紙面に重ねたまま残す。
            // 紙面を触る道具が入っている間は、タップが線や囲みになるので切り替えない。
            if (canHideChrome && !paperTool) setChromeHidden((hidden) => !hidden);
          }}
        >
          <div
            ref={scrollRef}
            className={zoom > 1 ? "study-viewer-scroll is-zoomed" : "study-viewer-scroll"}
            style={{ "--viewer-zoom": zoom } as CSSProperties}
          >
            <div className="study-viewer-canvas">
              {stageMessage ? (
                stageMessage
              ) : image.status === "error" ? (
                <p role="alert" className="study-viewer-msg">
                  画像を読み込めませんでした。
                </p>
              ) : !imageUrl ? (
                <p role="status" className="study-viewer-msg">
                  画像を読み込んでいます...
                </p>
              ) : spreadShown && spread ? (
                // 見開きは2枚を縦横比のとおりの幅で並べ、紙面の枠に収まる最大の大きさにする。
                <div
                  ref={spreadRef}
                  className="study-viewer-spread"
                  style={{ "--spread-ratio": aspects.main + aspects.pair } as CSSProperties}
                >
                  {spread.onLeft ? pairLeaf(spread) : null}
                  <div className="study-viewer-leaf" style={{ flexGrow: aspects.main }}>
                    {mainImg}
                    {mainOverlay}
                  </div>
                  {spread.onLeft ? null : pairLeaf(spread)}
                </div>
              ) : (
                <>
                  {mainImg}
                  {mainOverlay}
                </>
              )}
            </div>
          </div>
          {waitingNext && !stageMessage ? (
            <span role="status" className="study-viewer-wait" aria-label="ページを読み込んでいます" />
          ) : null}
          {canZoom ? (
            <div className="study-viewer-zoom" role="group" aria-label="紙面の大きさ">
              <button
                type="button"
                className="study-viewer-btn study-viewer-zoom-btn"
                aria-label="紙面を小さくする"
                onClick={() => setZoomStep((i) => Math.max(0, i - 1))}
                disabled={zoomStep === 0}
              >
                <span aria-hidden="true">−</span>
              </button>
              <button
                type="button"
                className="study-viewer-btn study-viewer-zoom-btn study-viewer-zoom-now"
                aria-label="紙面をもとの大きさにもどす"
                onClick={() => setZoomStep(0)}
                disabled={zoomStep === 0}
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                className="study-viewer-btn study-viewer-zoom-btn"
                aria-label="紙面を大きくする"
                onClick={() => setZoomStep((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
                disabled={zoomStep === ZOOM_STEPS.length - 1}
              >
                <span aria-hidden="true">＋</span>
              </button>
            </div>
          ) : null}
          {hasNav && !paperTool && navRoom !== false ? (
            <div className="study-viewer-nav">
              <button
                type="button"
                className="study-viewer-btn study-viewer-nav-btn"
                aria-label="前へ"
                onClick={onPrev}
                disabled={!hasPrev}
              >
                <span aria-hidden="true">←</span>
                <span className="study-viewer-btn-label"> 前へ</span>
              </button>
              <button
                type="button"
                className="study-viewer-btn study-viewer-nav-btn"
                aria-label="次へ"
                onClick={onNext}
                disabled={!hasNext}
              >
                <span className="study-viewer-btn-label">次へ </span>
                <span aria-hidden="true">→</span>
              </button>
            </div>
          ) : null}
        </div>
        {sidePane !== undefined ? <div className="study-viewer-side">{sidePane}</div> : null}
        {openPanel && sidePane === undefined ? (
          <div className="study-viewer-side">{openPanel.content}</div>
        ) : null}
        {persistentPane ? (
          <div className="study-viewer-side" hidden={!persistentVisible || sidePane !== undefined}>
            {persistentPane.content}
          </div>
        ) : null}
      </div>
      {bottomBar ? <div className="study-viewer-bottombar">{bottomBar}</div> : null}
      {/* 読むだけのときは真ん中のタップで操作を戻せるので、つまみを出さない。
          道具が入っている間はタップが線や囲みになるので、つまみで戻す。 */}
      {bare && paperTool ? (
        <div className="study-viewer-pull-group">
          {hasNav && paperTool ? (
            <button
              type="button"
              className="study-viewer-pull study-viewer-pull-nav"
              aria-label="前へ"
              onClick={onPrev}
              disabled={!hasPrev}
            >
              <span aria-hidden="true">←</span>
            </button>
          ) : null}
          <button
            type="button"
            className="study-viewer-pull"
            aria-label="操作を出す"
            onClick={() => setChromeHidden(false)}
          >
            <PullDownIcon />
          </button>
          {hasNav && paperTool ? (
            <button
              type="button"
              className="study-viewer-pull study-viewer-pull-nav"
              aria-label="次へ"
              onClick={onNext}
              disabled={!hasNext}
            >
              <span aria-hidden="true">→</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
